/**
 * 猫の名前と、気持ちの吹き出し（§2.1 の延長）。
 *
 * 吹き出しに入るのは記号だけで、台詞は入れない。
 * 「猫が何を考えているか」を言葉にした瞬間、猫がプレイヤーに話しかける存在になり、
 * §2.3（間接的にしか関われない）と §1（台詞で語らない）が崩れる。
 */
import { CatState } from '../sim/state';

export type Mood = 'sleep' | 'happy' | 'play' | 'love' | 'surprise' | 'confused' | 'sulk' | 'wait' | null;

/** その猫の今の気分。姿だけでは伝わりにくいものだけ出す */
export function moodOf(cat: CatState): Mood {
  if (cat.busyKind === 'sleep') return 'sleep';
  if (cat.startled > 0) return 'surprise';
  if (cat.stuck && cat.blocked > 2) return 'sulk';
  if (cat.busyKind === 'tussle' || cat.busyKind === 'play' || cat.busyKind === 'swat') return 'play';
  if (cat.busyKind === 'groom' || cat.busyKind === 'loaf' || cat.busyKind === 'meet') return 'happy';
  if (cat.busyKind === 'watch') return 'love';
  if (cat.waiting > 0.4) return 'wait';
  if (cat.path === null && cat.mode === 'idle' && cat.goalReason === 'none') return null;
  return null;
}

const BUBBLE_BG = 'rgba(20,25,32,.85)';
const INK = '#dfe6ee';

/** 気持ちの吹き出し。記号だけ */
export function drawMood(ctx: CanvasRenderingContext2D, cat: CatState, mood: Mood): void {
  if (!mood) return;
  const x = cat.x + 13;
  const y = cat.y - 19;

  ctx.save();
  ctx.beginPath();
  ctx.ellipse(x, y, 9, 7, 0, 0, Math.PI * 2);
  ctx.fillStyle = BUBBLE_BG;
  ctx.fill();
  // しっぽ（吹き出しの向き）
  ctx.beginPath();
  ctx.moveTo(x - 6, y + 5);
  ctx.lineTo(x - 10, y + 10);
  ctx.lineTo(x - 3, y + 7);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = INK;
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.2;
  ctx.lineCap = 'round';

  switch (mood) {
    case 'sleep': {
      ctx.font = 'bold 7px ui-monospace,monospace';
      ctx.textAlign = 'center';
      ctx.fillText('z', x - 3, y + 1);
      ctx.font = 'bold 9px ui-monospace,monospace';
      ctx.fillText('z', x + 3, y - 1);
      break;
    }
    case 'happy': {
      // 目を細めた顔
      ctx.beginPath();
      ctx.arc(x - 3, y - 1, 2, 0.15 * Math.PI, 0.85 * Math.PI);
      ctx.moveTo(x + 5, y - 1);
      ctx.arc(x + 3, y - 1, 2, 0.15 * Math.PI, 0.85 * Math.PI);
      ctx.stroke();
      break;
    }
    case 'play': {
      // 音符
      ctx.beginPath();
      ctx.ellipse(x - 2, y + 3, 2.4, 1.8, -0.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(x, y + 3);
      ctx.lineTo(x + 1, y - 5);
      ctx.lineTo(x + 5, y - 4);
      ctx.stroke();
      break;
    }
    case 'love': {
      ctx.beginPath();
      ctx.moveTo(x, y + 4);
      ctx.bezierCurveTo(x - 7, y - 2, x - 3, y - 6, x, y - 2);
      ctx.bezierCurveTo(x + 3, y - 6, x + 7, y - 2, x, y + 4);
      ctx.fill();
      break;
    }
    case 'surprise': {
      ctx.fillRect(x - 1, y - 5, 2.2, 6);
      ctx.fillRect(x - 1, y + 3, 2.2, 2.2);
      break;
    }
    case 'confused': {
      ctx.font = 'bold 11px ui-monospace,monospace';
      ctx.textAlign = 'center';
      ctx.fillText('?', x, y + 4);
      break;
    }
    case 'sulk': {
      // への字
      ctx.beginPath();
      ctx.moveTo(x - 5, y + 2);
      ctx.quadraticCurveTo(x, y - 3, x + 5, y + 2);
      ctx.stroke();
      break;
    }
    case 'wait': {
      for (let i = -1; i <= 1; i++) {
        ctx.beginPath();
        ctx.arc(x + i * 4, y, 1.1, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
  }
  ctx.restore();
}

/** 名前の札。寄っている時と、選んでいる猫にだけ出す */
export function drawName(ctx: CanvasRenderingContext2D, cat: CatState, highlight: boolean): void {
  ctx.save();
  ctx.font = `${highlight ? 'bold ' : ''}8px "Hiragino Sans","Noto Sans JP",system-ui,sans-serif`;
  ctx.textAlign = 'center';
  const w = ctx.measureText(cat.name).width + 8;
  const y = cat.y + 9;
  ctx.fillStyle = highlight ? 'rgba(95,211,189,.9)' : 'rgba(20,25,32,.6)';
  ctx.fillRect(cat.x - w / 2, y - 7, w, 10);
  ctx.fillStyle = highlight ? '#0e1116' : 'rgba(195,204,214,.85)';
  ctx.fillText(cat.name, cat.x, y);
  ctx.restore();
}
